// src/components/blog/BlogHero.jsx
import React from 'react';
import { motion } from 'framer-motion';
import { designTokens } from '../../theme/designTokens';
import { Calendar, Clock, Eye, User, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import {getCategoryColor} from "../../utils/blogUtils";

const BlogHero = ({
                      title,
                      excerpt,
                      category,
                      author,
                      date,
                      readTime,
                      views,
                      showBackButton = true
                  }) => {
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            style={{
                position: 'relative',
                padding: '4rem 2rem',
                background: designTokens.colors.gradients.luxury,
                overflow: 'hidden',
            }}
        >
            {/* Background Pattern */}
            <div style={{
                position: 'absolute',
                inset: 0,
                backgroundImage: `radial-gradient(circle at 20% 30%, ${designTokens.colors.champagne}05 2px, transparent 2px)`,
                backgroundSize: '50px 50px',
                opacity: 0.1,
            }} />

            <div style={{
                maxWidth: '800px',
                margin: '0 auto',
                position: 'relative',
                zIndex: 1,
            }}>
                {/* Back Navigation */}
                {showBackButton && (
                    <Link
                        to="/blog"
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            color: designTokens.colors.text.secondary,
                            textDecoration: 'none',
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            marginBottom: '2rem',
                            transition: 'color 0.2s ease',
                        }}
                        onMouseEnter={(e) => e.target.style.color = designTokens.colors.champagne}
                        onMouseLeave={(e) => e.target.style.color = designTokens.colors.text.secondary}
                    >
                        <ArrowLeft size={16} />
                        Back to Articles
                    </Link>
                )}

                {/* Category */}
                <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.5rem 1rem',
                    borderRadius: '9999px',
                    background: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    border: `1px solid ${designTokens.colors.border.light}`,
                    marginBottom: '1.5rem',
                }}>
          <span style={{
              fontSize: '0.75rem',
              fontWeight: 600,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: getCategoryColor(category),
          }}>
            {category}
          </span>
                </div>

                {/* Title */}
                <h1 style={{
                    fontFamily: designTokens.typography.fontFamilies.display,
                    fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
                    fontWeight: 300,
                    color: designTokens.colors.text.primary,
                    lineHeight: 1.2,
                    marginBottom: '1.5rem',
                    letterSpacing: '-0.02em',
                }}>
                    {title}
                </h1>

                {/* Excerpt */}
                <p style={{
                    fontSize: '1.25rem',
                    color: designTokens.colors.text.secondary,
                    lineHeight: 1.6,
                    marginBottom: '2rem',
                }}>
                    {excerpt}
                </p>

                {/* Meta Information */}
                <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: '1.5rem',
                    padding: '1rem 0',
                    borderTop: `1px solid ${designTokens.colors.border.light}`,
                    borderBottom: `1px solid ${designTokens.colors.border.light}`,
                }}>
                    {/* Author */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <div style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            background: `${designTokens.colors.champagne}15`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                            <User size={16} color={designTokens.colors.champagne} />
                        </div>
                        <div>
                            <div style={{
                                fontSize: '0.875rem',
                                fontWeight: 500,
                                color: designTokens.colors.text.primary,
                            }}>
                                {author || 'KROMAIC Expert'}
                            </div>
                            <div style={{
                                fontSize: '0.75rem',
                                color: designTokens.colors.text.tertiary,
                            }}>
                                Certified Skincare Specialist
                            </div>
                        </div>
                    </div>

                    {/* Date */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Calendar size={14} color={designTokens.colors.text.tertiary} />
                        <span style={{
                            fontSize: '0.875rem',
                            color: designTokens.colors.text.secondary,
                        }}>
              {new Date(date).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric'
              })}
            </span>
                    </div>

                    {/* Read Time */}
                    {readTime && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Clock size={14} color={designTokens.colors.text.tertiary} />
                            <span style={{
                                fontSize: '0.875rem',
                                color: designTokens.colors.text.secondary,
                            }}>
                {readTime} min read
              </span>
                        </div>
                    )}

                    {/* Views */}
                    {views && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Eye size={14} color={designTokens.colors.text.tertiary} />
                            <span style={{
                                fontSize: '0.875rem',
                                color: designTokens.colors.text.secondary,
                            }}>
                {views.toLocaleString()} views
              </span>
                        </div>
                    )}
                </div>
            </div>
        </motion.div>
    );
};

export default BlogHero;